const CartLine = require('@/lib/Cart/CartLine');
const EventNames = require('@/lib/Events/EventNames');
const Membership = require('@/lib/Memberships/Membership');
const MembershipRefundAmount = require('@/lib/Memberships/MembershipRefundAmount');
const { cloneDeep } = require('@/functions/clone');
const { container } = require('@/container');
const { negateObjectValues } = require('@/functions/math');

/**
 * @constructor
 *
 * @param $location
 * @param {Hydrator} hydrator
 * @param membershipResource
 * @param membershipSubscriptionResource
 * @param {ToastFactory} toastFactory
 * @param {UserService} userService
 */
const MembershipService = function (
    $location,
    hydrator,
    membershipResource,
    membershipSubscriptionResource,
    toastFactory,
    userService,
) {
    this.$location = $location;
    this.hydrator = hydrator;
    this.membershipResource = membershipResource;
    this.membershipSubscriptionResource = membershipSubscriptionResource;
    this.toastFactory = toastFactory;
    this.userService = userService;
};

MembershipService.prototype = {
    /**
     * Retrieve a Membership from the API.
     *
     * @param {string} ref
     * @param {string} [apiResolvers]
     *
     * @returns {Promise<Membership>}
     */
    getMembershipByRef(ref, apiResolvers) {
        return new Promise((resolve, reject) => {
            if (apiResolvers === undefined) {
                apiResolvers = '*';
            }

            this.membershipResource.query(
                {
                    ref,
                    resolve: apiResolvers,
                    limit: 1,
                },
                (results) => {
                    if (results.length > 0) {
                        let membership = this.hydrator.hydrate(results[0], new Membership());
                        resolve(membership);
                    } else {
                        reject();
                    }
                },
                () => {
                    reject();
                }
            );
        });
    },

    /**
     * Send the updated dataset to the API to update a membership.
     *
     * @param {Membership} membership
     * @param {MembershipDataset} membershipDataset
     * @param {Function} [callback]
     */
    updateMembership(membership, membershipDataset, callback) {
        let data = membershipDataset.toServerData();

        membership.currentlyProcessing = true;
        this.membershipResource.update(
            {
                membershipID: membership.getID(),
                resolve: '*',
            },
            data,
            (response) => {
                membership.currentlyProcessing = false;
                this.hydrator.hydrate(response, membership);
                if (typeof callback === 'function') {
                    callback(membership);
                }
                container.eventBus.broadcast(EventNames.MEMBERSHIP_REDEMPTION_ACTIVITY);
            },
            (response) => {
                let error = response.data.message ? response.data.message : response.data.error;
                this.toastFactory.errorTop(
                    'MEMBERSHIPS.ERROR_TITLE__MEMBERSHIP_SAVE',
                    error
                );

                membership.currentlyProcessing = false;
            }
        );
    },

    /**
     * Method to generate all the data for memberships, which will get sent to the server.
     * The return object is an array. Each element in the array represents a single membership.
     * Its properties are: a membership plan id, any overridden membership reference, an
     * address object and a members list.
     *
     * @param {CartLine[]} cartItems
     *
     * @returns []
     */
    getDataForServer(cartItems) {
        let membershipData = [];

        // Loop through all the cart items (order lines)...
        cartItems.forEach(
            function (cartItem) {
                // If this line is a plan (with possibly multiple memberships) and we are
                // not renewing existing membership(s)...
                if (cartItem.isMembershipPlan() && !cartItem.renewingMembership) {
                    // Take a copy of its membership(s) data.
                    /**
                     * @type {MembershipDataset[]}
                     */
                    let membershipDatasets = cloneDeep(cartItem.membershipDatasets);

                    // Loop through each membership...
                    membershipDatasets.forEach(
                        function (membershipDataset) {
                            let membershipDatasetData = membershipDataset.toServerData();
                            if (cartItem.getQuantity() < 1) {
                                membershipDatasetData.isCancelling = true;
                            }
                            membershipData.push(membershipDatasetData);
                        }
                    );
                }
            }
        );

        return membershipData;
    },

    /**
     * Calculate the refund for multiple memberships.
     *
     * @param {Membership[]} memberships
     * @param {Function} callback
     */
    calculateMembershipRefunds(memberships, callback) {
        let membershipIDs = memberships.map(function (membership) {
            return membership.ID;
        });

        this.membershipResource.getRefunds(
            {
                'membershipIDs[]': membershipIDs,
            },
            (response) => {
                for (let i = 0; i < memberships.length; i++) {
                    let membership = memberships[i];
                    if (response.hasOwnProperty(membership.ID)) {
                        membership.refundAmount = this.hydrator.hydrate(
                            response[membership.ID],
                            new MembershipRefundAmount()
                        );
                    }
                }

                callback(memberships);
            }
        );
    },

    /**
     * @param {Membership[]} memberships
     * @param {Function} callback
     */
    cancelMemberships(memberships, callback) {
        let self = this;

        let membershipIDs = this.mapMembershipIds(memberships);

        this.membershipResource.cancelMultiple(
            {
                'membershipIDs[]': membershipIDs,
            },
            function (response) {
                self.rehydrateModels(
                    memberships,
                    response,
                    'membershipID'
                );

                callback(response, memberships);
            },
            function () {
                callback(null, memberships);
            }
        );
    },

    /**
     * @param {Membership[]|MembershipSubscription[]} models
     * @param {object[]} data
     * @param {string} idPropertyInData
     */
    rehydrateModels(models, data, idPropertyInData) {
        for (let i = 0; i < data.length; i++) {
            let ID = data[i][idPropertyInData];
            let model = models.find((m) => m.ID === ID);
            if (model) {
                this.hydrator.hydrate(
                    data[i],
                    model
                );
            }
        }
    },

    stopRenewingMemberships(memberships, callback) {
        let self = this;

        let membershipIDs = memberships.map(function (membership) {
            return membership.ID;
        });

        this.membershipResource.updateMultiple(
            {
                membershipIDs,
                stopRenewing: 1,
                resolve: '*',
            },
            function (response) {
                if (response) {
                    self.rehydrateModels(
                        memberships,
                        response,
                        'membershipID'
                    );
                }

                callback(memberships);
            },
            function () {
                callback(memberships);
            }
        );
    },

    /**
     * @param {Membership[]} memberships
     * @param {Object<string>} refundTypes An object where membership IDs are the key and a value from
     *                                             MembershipRefundType is the value.
     * @param {string|null} defaultRefundType Refund type to use if the membershipID was
     *                                        not present in refundTypes. See MembershipRefundType
     *
     * @return {CartLine[]}
     */
    generateMembershipRefundCartItems(
        memberships,
        refundTypes,
        defaultRefundType
    ) {
        let cartItems = [];

        for (let i = 0; i < memberships.length; i++) {
            let membership = memberships[i];

            let refundType = refundTypes.hasOwnProperty(membership.ID) ? refundTypes[membership.ID] : defaultRefundType;
            if (!refundType || !membership.refundAmount || !membership.refundAmount.hasOwnProperty(refundType)) {
                continue;
            }

            let refundAmount = membership.refundAmount[refundType + 'Total'];
            if (refundAmount <= 0) {
                // Sometimes we want zero refunds to be included just to visually show they're being
                // cancelled / returned. This is now required as the SellCtrl triggers the cancellation,
                // so needs a cartItem for each membership to cancel.
                refundAmount = 0.00;
            }

            /**
             * How the refund amount is split between different subscriptions.
             *
             * @type {Object<number>}
             */
            let subscriptionApportionment = membership.refundAmount[refundType];

            // The refund line uses the membership's current MembershipPlan as the item.
            let cartItem = new CartLine(
                null,
                membership.currentPlan,
                // Use the order branch because refunding memberships happens off of the sell page where the item
                // branch can be selected.
                this.userService.currentBranch
            );
            // Note that the price is an absolute value, so the qty need to be -1.
            cartItem.setPrice(refundAmount);
            cartItem.adjustQuantity(-1);
            cartItem.setCanAdjustQuantity(false);
            cartItem.setCanRemove(false);
            cartItem.setMembershipSubscriptionApportionedAmounts(
                {
                    // Amounts for instance 1 (there's only one instance on the line)
                    1: negateObjectValues(subscriptionApportionment),
                }
            );
            cartItem.setCancellingMembership(membership);
            cartItem.addMembershipDatasetForRefund(membership.toMembershipDataset());
            cartItem.calculate();

            cartItems.push(cartItem);
        }

        return cartItems;
    },

    /**
     * Generate a comma separated string of all the refs for the memberships in the given array.
     *
     * @param {Membership[]} memberships
     *
     * @return {string}
     */
    generateMembershipRefsString(memberships) {
        let refs = memberships.map(function (membership) {
            return membership.ref;
        });

        return refs.join(', ');
    },

    /**
     * @param {MembershipSubscription[]} subscriptions
     * @param {function} callback
     */
    saveNewSubscriptionInstallmentAmounts(subscriptions, callback) {
        let self = this;

        let newInstallmentAmounts = {};

        for (let i = 0; i < subscriptions.length; i++) {
            newInstallmentAmounts[subscriptions[i].ID] = subscriptions[i].newInstallmentAmount;
        }

        let membershipSubscriptionIds = Object.keys(newInstallmentAmounts);

        this.membershipSubscriptionResource.updateMultiple(
            {},
            {
                membershipSubscriptionIDs: membershipSubscriptionIds,
                installmentAmounts: newInstallmentAmounts,
            },
            function (response) {
                // Re-hydrate the models with the new data.
                self.rehydrateModels(
                    subscriptions,
                    response,
                    'membershipSubscriptionID'
                );

                callback(subscriptions, newInstallmentAmounts);
            },
            function () {
                callback(subscriptions, newInstallmentAmounts);
            }
        );
    },

    /**
     * @param {Membership[]} memberships
     *
     * @return {number[]}
     */
    mapMembershipIds(memberships) {
        return memberships.map((membership) => membership.ID);
    },

    /**
     * @param {Membership[]} memberships
     *
     * @return {Membership[]}
     */
    extractAutoRenewingMemberships(memberships) {
        return memberships.filter((membership) => membership.willAutoRenew());
    },

    /**
     * If we are returning from successfully renewing the memberships on screen, show a nice notice.
     */
    showRenewalSuccessIfAppropriate() {
        const queryString = this.$location.search();
        if (queryString.hasOwnProperty('renewalSuccess') && !!queryString.renewalSuccess) {
            this.toastFactory.successTop('MEMBERSHIPS.RENEWED_TOAST_TITLE');
        }
    },

};

module.exports = MembershipService;
